import { useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import Modal from '../components/ui/Modal';
import AppLoadingOverlay from '../components/ui/AppLoadingOverlay';
import DocumentPreviewModal from '../components/ui/DocumentPreviewModal';
import PdfDocumentPreview from '../components/ui/PdfDocumentPreview';
import ClientIntakeDocuments from '../components/clients/ClientIntakeDocuments';
import CountrySelect from '../components/ui/CountrySelect';
import KycDocumentOwnerChooser from './KycDocumentOwnerChooser';
import { categoryLabels, intakeLabels, intakeReviewValueError, cvSourceLabels } from '../utils/clientDocumentIntake';
import { kycIntakeRows } from './kycDocumentIntake';
import { useLockBodyScroll } from './sections/common/common_utils/useLockBodyScroll';
import nameIcon from '../images/individual-lottie.json';
import entityIcon from '../images/building-lottie.json';
import dobIcon from '../images/birthday.svg';
import nationalityIcon from '../images/passport.svg';
import addressIcon from '../images/address.svg';
import countryIcon from '../images/earth-location.svg';
import emailIcon from '../images/email_icon.svg';
import phoneIcon from '../images/phone-call.svg';
import activityIcon from '../images/briefcaseX.svg';
import '../components/clients/AddClientModal.css';
import '../pages/css/StaffDocumentsReviewPage.css';
import './KycDocumentIntakeModal.css';

const icons={dateOfBirth:dobIcon,incorporationDate:dobIcon,nationality:nationalityIcon,address:addressIcon,city:addressIcon,country:countryIcon,contactEmail:emailIcon,contactPhone:phoneIcon,businessActivity:activityIcon};
const countryNames=new Intl.DisplayNames(['en'],{type:'region'});
const label = field => field.startsWith('wealth:') ? 'Source of wealth' : ({businessActivity:'Business activity',contactEmail:'Email',contactPhone:'Phone',clientName:'Name'}[field] || intakeLabels[field] || field).replace(' (KYC)','');
const format = (field,value) => {
  if(!value) return 'Leave blank';
  if(field.startsWith('wealth:')) return cvSourceLabels[value] || value;
  if(['country','nationality','incorporationCountry'].includes(field) && /^[A-Z]{2}$/.test(value)) return countryNames.of(value);
  if(['dateOfBirth','incorporationDate'].includes(field) && /^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
  if(field==='clientType') return value==='entity' ? 'Entity' : 'Individual';
  return value;
};
const keyFor = option => `${option.documentId}:${option.value}`;

export default function KycDocumentIntakeModal({intake,form: suppliedForm,separateNames = false}) {
  const form = intake.reviewForm || suppliedForm;
  useLockBodyScroll(true, { strategy: 'overflow' });
  const [choices,setChoices]=useState({});
  const [edits,setEdits]=useState({});
  const [preview,setPreview]=useState(null);
  useEffect(()=>{
    if(!preview) return undefined;
    const closePreview=event=>{if(event.key==='Escape') setPreview(null);};
    window.addEventListener('keydown',closePreview);
    return ()=>window.removeEventListener('keydown',closePreview);
  },[preview]);
  const {session}=intake;
  const documents=session.documents;
  const attachments=documents.filter(document=>!document.attachmentRemoved);
  const rows=kycIntakeRows(documents,form,separateNames);
  const newRows=rows.filter(row=>!row.currentValue);
  const comparison=rows.filter(row=>!!row.currentValue);
  const selected = row => {
    const explicit=choices[row.field];
    if(explicit!==undefined) return row.options.find(o=>keyFor(o)===explicit) || null;
    return !row.currentValue && row.options.length===1 ? row.options[0] : null;
  };
  const values={}; const errors={};
  rows.forEach(row=>{
    const option=selected(row);
    if(!option) return;
    const editKey=`${row.field}:${keyFor(option)}`;
    const value=edits[editKey] ?? option.value;
    if(!row.field.startsWith('wealth:') && row.field!=='businessActivity') errors[row.field]=intakeReviewValueError(row.field,value);
    if(value) values[row.field]=value;
  });
  const disabled=session.busy || session.saving;
  const activeDocument = documents[0];
  useEffect(() => { setChoices({}); setEdits({}); }, [activeDocument?.id, intake.target?.id]);
  function fieldHeading(row) {
    return <div className="kycIntakeFieldHeading">
      <span className="kycIntakeFieldIcon">{['clientName','clientType'].includes(row.field)
        ? <Lottie animationData={form.customer?.type==='entity' ? entityIcon : nameIcon} loop={false} autoplay={false}/>
        : <img src={icons[row.field] || activityIcon} alt=""/>}</span>
      <span>{label(row.field)}</span>
    </div>;
  }
  function renderRow(row) {
    const chosen=selected(row);
    const needsEdit=chosen && (chosen.uncertain || chosen.confidence<.85 || row.issues.length>0);
    const editKey=chosen ? `${row.field}:${keyFor(chosen)}` : '';
    return <section key={row.field} className={`kycIntakeField${row.field === 'businessActivity' && row.currentValue ? ' kycIntakeField--stacked' : ''}`}>
      {fieldHeading(row)}
      <div className="intakeReviewOptions" role="group" aria-label={label(row.field)}>
        {row.currentValue && <button type="button" className="intakeReviewOption" aria-pressed={!chosen} onClick={()=>setChoices(prev=>({...prev,[row.field]:''}))}>
          <span className="intakeReviewOptionSource">Current details</span>
          <span className="intakeReviewOptionValue"><i className={!chosen ? 'bi bi-check-circle-fill':'bi bi-circle'} aria-hidden="true"/>{format(row.field,row.currentValue)}</span>
        </button>}
        {row.options.map(option=>{
          const checked=chosen===option; const doc=documents.find(d=>d.id===option.documentId);
          return <div key={keyFor(option)} className="kycIntakeSourceCard">
          <button type="button" className="intakeReviewOption" role={row.currentValue ? undefined:'checkbox'} aria-checked={row.currentValue ? undefined:checked} aria-pressed={row.currentValue ? checked:undefined}
            onClick={()=>setChoices(prev=>({...prev,[row.field]:checked && !row.currentValue ? '' : keyFor(option)}))}>
            <span className="intakeReviewOptionSource">From {categoryLabels[doc?.category] || 'document'}</span>
            <span className="intakeReviewOptionValue"><i className={checked ? 'bi bi-check-circle-fill':'bi bi-circle'} aria-hidden="true"/>{format(row.field,option.value)}</span>
          </button>
          {doc?.file && <button type="button" className="intakeReviewTextButton kycIntakeSourcePreview" aria-label={`View ${categoryLabels[doc.category]}`} title={`View ${categoryLabels[doc.category]}`} onClick={()=>setPreview(doc)}><i className="bi bi-eye" aria-hidden="true"/></button>}
          </div>;
        })}
      </div>
      {row.explanation && <p className="kycIntakeHint">{row.explanation}</p>}
      {(row.uncertain || row.issues.length>0) && <p className="kycIntakeHint">Check this detail against the document before applying it.</p>}
      {needsEdit && row.field!=='clientType' && <div className="intakeReviewEdit">
        {['country','nationality','incorporationCountry'].includes(row.field) ? <CountrySelect value={edits[editKey] ?? chosen.value} onChange={value=>setEdits(prev=>({...prev,[editKey]:value || ''}))}/>
          : <input aria-label={`Correct ${label(row.field)}`} className="form-control" type={['dateOfBirth','incorporationDate'].includes(row.field)?'date':'text'} value={edits[editKey] ?? chosen.value} onChange={e=>setEdits(prev=>({...prev,[editKey]:e.target.value}))}/>}
      </div>}
      {errors[row.field] && <small role="alert">{errors[row.field]}</small>}
    </section>;
  }
  if(preview) return <DocumentPreviewModal open title={categoryLabels[preview.category]} file={{contentType:preview.file.contentType,originalName:categoryLabels[preview.category],downloadURL:`data:${preview.file.contentType};base64,${preview.file.dataBase64}`}} pdfRenderer={PdfDocumentPreview} showOpenInNewTab={false} shellHeight="min(76vh, 720px)" style={{'--doc-preview-stack-height':'min(76vh, 720px)'}} onClose={()=>setPreview(null)}/>;
  if(session.notice) return <Modal open title="Document not identified" className="clientIntakeReview kycIntakeNotice" onClose={intake.dismissNotice} footer={<button type="button" className="btn btn-modal-save" onClick={intake.dismissNotice}>OK</button>}><p>{session.notice}</p></Modal>;
  if(session.busy) return <div className="kycIntakeLoading" role="status" aria-live="polite" aria-label={session.message}>
    <AppLoadingOverlay title={session.message} loaderStyle="dots" bg="transparent"/>
  </div>;
  if (activeDocument && intake.targets && (!intake.target || session.choosingTarget)) return <Modal open title="Who does this document belong to?" className="clientIntakeReview kycIntakeModal kycIntakeModal--owner" onClose={intake.close}
    footer={<button type="button" className="btn btn-modal-cancel" onClick={intake.close}>Cancel</button>}>
    <KycDocumentOwnerChooser document={activeDocument} targets={intake.targets} onChoose={intake.chooseTarget}/>
  </Modal>;
  return <Modal open title={rows.length ? 'Review document details':'Attached documents'} className={`clientIntakeReview kycIntakeModal${!rows.length?' kycIntakeModal--documents':''}`} onClose={disabled ? ()=>{} : intake.close}
    footer={<>
      <button type="button" className="btn btn-modal-save" disabled={disabled || session.saveDisabled || (!rows.length && !attachments.length) || Object.values(errors).some(Boolean)} onClick={()=>intake.finish(values,attachments,!Object.keys(values).length)}>
        {session.saving ? 'Saving…' : rows.length ? 'Apply selected details' : attachments.length===1 ? 'Save document':'Save documents'}
      </button>
      <button type="button" className="btn btn-modal-cancel" disabled={disabled} onClick={intake.close}>Cancel</button>
    </>}>
      {intake.target && <div className="kycIntakeTargetBanner"><div><strong>{intake.target.name}</strong><span>{[intake.target.role, categoryLabels[activeDocument?.category]].filter(Boolean).join(' · ')}</span></div>
        {!intake.targetLocked && <button type="button" className="kycIntakeTextLink" disabled={disabled} onClick={intake.changeTarget}>Change person / entity</button>}
      </div>}
      {session.queue?.length > 1 && <p>{session.queue.length} documents remaining to review.</p>}
      {session.error && <p role="alert">{session.error}</p>}
      {activeDocument && !rows.length && <p>No new or different details were found. You can save the document for this person or entity.</p>}
      {activeDocument?.category === 'sow_document' && <p>This supporting document will be saved under Other documents. Review its contents before marking a source-of-wealth requirement complete.</p>}
      {!!newRows.length && <section><h3 className="kycIntakeSectionTitle">New details</h3>{newRows.map(renderRow)}</section>}
      {!!comparison.length && <section><h3 className="kycIntakeSectionTitle">Choose the correct details</h3>{comparison.map(renderRow)}</section>}
      {!!attachments.length && <ClientIntakeDocuments documents={attachments} showTitle={!!rows.length} onPreview={setPreview} onRemove={intake.remove} removalDisabled={disabled || intake.targetLocked}/>}
      {!attachments.length && !rows.length && !session.error && <p>No documents to save.</p>}
  </Modal>;
}
